import { getTrainingEvalRunSummaries, type TrainingEvalRunSummaryView } from "@/api/trainingEval";
import { registerSessionCacheReset } from "@/api/sessionCatalogs";
import { assignChartSeriesSlots } from "@/shared/ui/chart/public";

/**
 * eval 뷰(상세·비교·병합 side panel)의 공용 상태다. 뷰 자체는 Evaluation Results 창의 로컬
 * 상태(`EvaluationResultsWindowContent`)가 소유한다. 이 모듈은 창이 가질 수 없는 두 가지만 둔다:
 * - 이전 딥링크 복원에 쓰는 summary 조회
 * - 뷰와 창 사이에서 유지하는 비교 색 슬롯 기억(prevSlots)
 */

// 이전 딥링크의 summary 를 한 번에 조회한다(1 round-trip, §7). 호출부(useLegacyResultsDeepLink)가
// 1회 사용 가드를 가지므로 같은 키로 중복 호출하지 않는다.
export async function loadEvalRunSummaries(
  ids: readonly string[],
): Promise<TrainingEvalRunSummaryView[]> {
  const runs = await getTrainingEvalRunSummaries(ids);
  // 일부 id 를 찾지 못하면 뷰를 그릴 수 없다. 복원 실패로 처리하고 호출부가 정리한다.
  if (runs.length !== ids.length) {
    throw new Error("일부 Evaluation 실행을 찾을 수 없습니다.");
  }
  return runs;
}

// 선택을 바꿔 다시 열어도 남아 있는 run 은 이전 색을 유지하도록 직전 슬롯 배정을 기억한다.
let prevSlots: Record<string, number> = {};
// canonical key(정렬 id join) 기준의 멱등 캐시다. 렌더 중 호출과 StrictMode 이중 렌더가 같은
// 뷰로 prevSlots 를 다시 갱신하지 않게 한다. 또한 뷰와 결과 표가 같은 객체를 받게 한다.
let lastSlots: { key: string; slots: Record<string, number> } | null = null;

export function evalViewSlots(evalRunIds: readonly string[]): Readonly<Record<string, number>> {
  const key = [...evalRunIds].sort().join("+");
  if (lastSlots !== null && lastSlots.key === key) {
    return lastSlots.slots;
  }
  const slots = assignChartSeriesSlots(evalRunIds, prevSlots);
  lastSlots = { key, slots };
  prevSlots = slots;
  return slots;
}

// 로그아웃 경계다. 색 기억은 계정 상태라서 세션과 함께 비운다.
registerSessionCacheReset(() => {
  prevSlots = {};
  lastSlots = null;
});
